import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { UpgradeCard, parsePlanLockError } from "@/components/UpgradeCard";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, AreaChart, Area,
} from "recharts";
import {
  TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, Calendar, ShieldCheck, FileBarChart,
  ArrowRight, FileDown, Sparkles,
} from "lucide-react";

function fmtUsd(n, opts = {}) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const v = Number(n);
  const { compact = false } = opts;
  if (compact) {
    if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
    if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  }
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}
function fmtMonth(m) {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleString("en-US", { month: "short", year: "2-digit" });
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

function DeltaPill({ value, invert = false }) {
  if (value === null || value === undefined) {
    return <span className="text-[10px] text-slate-400 ml-2">—</span>;
  }
  const positive = value > 0;
  // For some KPIs (AR days, denial rate) lower is better — invert color.
  const good = invert ? !positive : positive;
  const color = value === 0 ? "#64748B" : good ? "#10B981" : "#EF4444";
  const bg = value === 0 ? "rgba(100,116,139,0.12)" : good ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)";
  const Icon = value === 0 ? Minus : positive ? TrendingUp : TrendingDown;
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ml-2"
      style={{ color, background: bg }}
    >
      <Icon className="h-2.5 w-2.5" />
      {value > 0 ? "+" : ""}{value}%
    </span>
  );
}

function StatCard({ label, value, sublabel, delta, invert, testId }) {
  return (
    <div
      className="relative overflow-hidden rounded-xl p-4"
      style={{
        background: "linear-gradient(160deg, #0B1524 0%, #122236 55%, #14283F 100%)",
        border: "1px solid rgba(94,234,212,0.18)",
      }}
      data-testid={testId}
    >
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent 0%, rgba(20,184,166,0.55) 50%, transparent 100%)" }} />
      <div className="text-[10px] font-bold tracking-[0.18em] uppercase" style={{ color: "#5EEAD4" }}>
        {label}
      </div>
      <div className="mt-2 flex items-end gap-1">
        <div className="font-display text-2xl font-semibold" style={{ color: "#FFFFFF" }}>{value}</div>
        <DeltaPill value={delta} invert={invert} />
      </div>
      <div className="text-[11px] mt-1" style={{ color: "#94A3B8" }}>{sublabel}</div>
    </div>
  );
}

export default function AROverTimePage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [planLock, setPlanLock] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ar-over-time");
      setData(data);
      setPlanLock(null);
    } catch (err) {
      const lock = parsePlanLockError(err);
      if (lock) {
        setPlanLock(lock);
      } else {
        toast.error(err?.response?.data?.detail || "Could not load AR-over-time data.");
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/ar-over-time/report.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "rciq_ar_trend_report.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Board-Ready Trend Report downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate the report.");
    } finally {
      setDownloading(false);
    }
  };

  const monthly = data?.monthly_timeline || [];
  const history = data?.kpi_history || [];
  const latest = history[history.length - 1];
  const prior = history.length > 1 ? history[history.length - 2] : null;

  const chartData = useMemo(() => monthly.map(m => ({
    name: fmtMonth(m.month),
    avg_aging_days: m.avg_aging_days,
    denied_dollars: m.denied_dollars,
    recoverable_dollars: m.recoverable_dollars,
    volume: m.volume,
  })), [monthly]);

  const showEmpty = !loading && (data?.snapshot_count || 0) === 0;
  const showSingleUploadHint = !loading && (data?.snapshot_count || 0) === 1;

  if (planLock) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto py-8" data-testid="ar-over-time-plan-lock">
          <UpgradeCard
            feature={planLock.feature}
            featureLabel={planLock.featureLabel}
            description="Every upload becomes a permanent reporting snapshot. Track Days in AR, denial rate, and recoverable dollars across months, quarters, and years. Demonstrate measurable RCM improvement over time."
            requiredPlan={planLock.requiredPlan}
            currentPlan={planLock.currentPlan}
          />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="fade-up" data-testid="ar-over-time-page">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <span className="label-overline">Trends</span>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-100 mt-1.5 tracking-tight">
              AR Over Time
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Cross-upload trends of average days in AR, denial rate, and recoverable revenue.
              Built from de-identified aggregates only — fully PHI-safe.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={downloadReport}
              disabled={downloading || loading || (data?.snapshot_count || 0) === 0}
              className="ai-cta rounded-lg"
              data-testid="ar-over-time-download-pdf"
              title="Board-Ready 2-page PDF — for your CFO / leadership team"
            >
              {downloading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
              Board-Ready PDF
            </Button>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-lg" data-testid="ar-over-time-refresh">
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="surface-card p-10 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
            <p className="text-sm text-slate-500 mt-2">Loading historical snapshots…</p>
          </div>
        ) : showEmpty ? (
          <div className="surface-card p-10 text-center" data-testid="ar-over-time-empty">
            <FileBarChart className="h-10 w-10 mx-auto text-slate-300" />
            <h2 className="font-display text-xl text-slate-100 mt-3">No snapshots yet</h2>
            <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">
              Upload your first denial file to start building your AR trendline. Every upload after that adds a new historical snapshot.
            </p>
          </div>
        ) : (
          <>
            {/* Working Capital Released hero — only renders after 2+ snapshots */}
            {data?.working_capital && data.working_capital.estimated_dollars_released !== null && data.working_capital.estimated_dollars_released > 0 ? (
              <div
                data-testid="ar-working-capital-card"
                className="relative overflow-hidden rounded-2xl p-6 sm:p-7 mb-6"
                style={{
                  background: "linear-gradient(135deg, #0A192F 0%, #0F2238 50%, #0A1626 100%)",
                  border: "1px solid rgba(94,234,212,0.30)",
                }}
              >
                <div className="absolute -top-12 -right-10 h-52 w-52 rounded-full bg-teal-400/15 blur-3xl pointer-events-none" />
                <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
                  <div className="flex-1">
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-teal-400/15 border border-teal-400/30 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-teal-300">
                      <Sparkles className="h-3 w-3" />
                      Working Capital Reclaimed
                      <span className="text-teal-400/60">· Estimated</span>
                    </div>
                    <div className="mt-3 flex items-baseline gap-3 flex-wrap">
                      <span className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight" data-testid="ar-working-capital-amount">
                        {fmtUsd(data.working_capital.estimated_dollars_released, { compact: true })}
                      </span>
                      <span className="text-sm text-slate-300">released to operations</span>
                    </div>
                    <p className="mt-3 text-sm text-slate-300 max-w-2xl leading-relaxed">
                      Days in AR reduced from{" "}
                      <span className="font-semibold text-white">{data.working_capital.baseline_ar_days.toFixed(1)}</span>{" "}
                      to{" "}
                      <span className="font-semibold text-white">{data.working_capital.current_ar_days.toFixed(1)}</span>{" "}
                      — a{" "}
                      <span className="font-semibold text-teal-300">{data.working_capital.days_improvement.toFixed(1)}-day</span>{" "}
                      improvement since your first snapshot. At an estimated avg daily revenue of{" "}
                      <span className="font-semibold text-white">{fmtUsd(data.working_capital.estimated_avg_daily_revenue, { compact: true })}</span>,
                      this freed up the cash above for operations.
                    </p>
                  </div>
                </div>
              </div>
            ) : (data?.snapshot_count || 0) >= 1 && (data?.snapshot_count || 0) < 2 ? (
              <div className="rounded-xl p-4 mb-6 border border-dashed border-teal-500/30 bg-teal-500/5" data-testid="ar-working-capital-hint">
                <div className="flex items-center gap-3">
                  <Sparkles className="h-4 w-4 text-teal-400 flex-shrink-0" />
                  <p className="text-xs text-slate-300">
                    <span className="font-semibold text-white">Working Capital Reclaimed</span> unlocks after your 2nd upload —
                    we'll show the dollars freed up by AR-days improvement automatically.
                  </p>
                </div>
              </div>
            ) : null}

            {/* KPI strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="ar-kpi-strip">
              <StatCard
                label="Avg Days in AR"
                value={latest ? `${latest.avg_aging_days.toFixed(1)} d` : "—"}
                sublabel="Current snapshot"
                delta={latest?.delta_vs_prior?.avg_aging_days ?? null}
                invert
                testId="ar-stat-avg-aging"
              />
              <StatCard
                label="Denial Rate"
                value={latest && latest.denial_rate_pct !== null ? `${latest.denial_rate_pct.toFixed(2)}%` : "—"}
                sublabel="Of total submitted claims"
                delta={latest?.delta_vs_prior?.denial_rate_pct ?? null}
                invert
                testId="ar-stat-denial-rate"
              />
              <StatCard
                label="Total Denied"
                value={fmtUsd(latest?.total_denied_dollars, { compact: true })}
                sublabel="Dollars on this snapshot"
                delta={latest?.delta_vs_prior?.total_denied_dollars ?? null}
                invert
                testId="ar-stat-total-denied"
              />
              <StatCard
                label="Recoverable"
                value={fmtUsd(latest?.total_recoverable_dollars, { compact: true })}
                sublabel="What we can win back"
                delta={latest?.delta_vs_prior?.total_recoverable_dollars ?? null}
                testId="ar-stat-recoverable"
              />
            </div>

            {/* Charts */}
            <div className="grid lg:grid-cols-2 gap-4 mb-6">
              <div className="surface-card p-4" data-testid="ar-chart-aging">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-teal-700">AR Aging</div>
                    <h3 className="font-display text-base text-slate-900">Average days in AR by month</h3>
                  </div>
                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={chartData} margin={{ top: 8, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 11 }}
                      formatter={(v) => `${Number(v).toFixed(1)} days`}
                    />
                    <Line type="monotone" dataKey="avg_aging_days" stroke="#0F766E" strokeWidth={2}
                          dot={{ r: 3, fill: "#0F766E" }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="surface-card p-4" data-testid="ar-chart-dollars">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-teal-700">Dollars at Risk</div>
                    <h3 className="font-display text-base text-slate-900">Denied vs recoverable per month</h3>
                  </div>
                  <Calendar className="h-3.5 w-3.5 text-slate-400" />
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={chartData} margin={{ top: 8, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="g-denied" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#EF4444" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#EF4444" stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="g-rec" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0F766E" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#0F766E" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(v) => fmtUsd(v, { compact: true })} />
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 11 }}
                      formatter={(v) => fmtUsd(v)}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area type="monotone" dataKey="denied_dollars" stroke="#EF4444" fill="url(#g-denied)" strokeWidth={1.5} name="Denied $" />
                    <Area type="monotone" dataKey="recoverable_dollars" stroke="#0F766E" fill="url(#g-rec)" strokeWidth={1.5} name="Recoverable $" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Snapshot library */}
            <div className="surface-card p-4 mb-6" data-testid="ar-snapshot-library">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-teal-700">Snapshot Library</div>
                  <h3 className="font-display text-base text-slate-900">
                    Every upload, captured forever ({data?.snapshot_count || 0} {data?.snapshot_count === 1 ? "snapshot" : "snapshots"})
                  </h3>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="py-2 pr-3">Captured</th>
                      <th className="py-2 pr-3">Dataset</th>
                      <th className="py-2 pr-3 text-right">Volume</th>
                      <th className="py-2 pr-3 text-right">Avg AR</th>
                      <th className="py-2 pr-3 text-right">Denial Rate</th>
                      <th className="py-2 pr-3 text-right">Denied $</th>
                      <th className="py-2 pl-3 text-right">Recoverable $</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.snapshots || []).map((s) => (
                      <tr key={s.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`ar-snapshot-row-${s.id}`}>
                        <td className="py-2.5 pr-3 text-xs text-slate-700 tabular-nums">{fmtDate(s.captured_at)}</td>
                        <td className="py-2.5 pr-3 text-xs">
                          <div className="font-medium text-slate-900">{s.dataset_name || "—"}</div>
                          <div className="text-[10px] text-slate-400">{s.uploaded_by || "system"}</div>
                        </td>
                        <td className="py-2.5 pr-3 text-xs text-slate-700 text-right tabular-nums">{(s.metrics?.total_volume || 0).toLocaleString()}</td>
                        <td className="py-2.5 pr-3 text-xs text-slate-700 text-right tabular-nums">{s.metrics?.avg_aging_days?.toFixed(1) ?? "—"} d</td>
                        <td className="py-2.5 pr-3 text-xs text-slate-700 text-right tabular-nums">
                          {s.metrics?.denial_rate_pct !== null && s.metrics?.denial_rate_pct !== undefined
                            ? `${s.metrics.denial_rate_pct.toFixed(2)}%` : "—"}
                        </td>
                        <td className="py-2.5 pr-3 text-xs text-slate-700 text-right tabular-nums">{fmtUsd(s.metrics?.total_denied_dollars, { compact: true })}</td>
                        <td className="py-2.5 pl-3 text-xs font-semibold text-right tabular-nums" style={{ color: "#0F766E" }}>
                          {fmtUsd(s.metrics?.total_recoverable_dollars, { compact: true })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Single-upload nudge */}
            {showSingleUploadHint && (
              <div className="rounded-xl p-4 flex items-start gap-3"
                   style={{ background: "rgba(94,234,212,0.06)", border: "1px solid rgba(94,234,212,0.22)" }}
                   data-testid="ar-single-upload-hint">
                <ShieldCheck className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: "#5EEAD4" }} />
                <div className="text-xs leading-relaxed" style={{ color: "#CBD5E1" }}>
                  <strong className="text-white">Want a real trendline?</strong> Upload again next month — each upload adds a fresh snapshot and unlocks month-over-month deltas, payor-level evolution, and a board-ready 12-month AR trend.
                  No PHI required, your historical data is preserved forever.
                </div>
                <ArrowRight className="h-3.5 w-3.5 mt-1 text-teal-300" />
              </div>
            )}

            <p className="text-[10px] text-slate-500 mt-4">
              All trends computed from de-identified operational aggregates only. No PHI is stored, displayed, or processed.
            </p>
          </>
        )}
      </div>
    </AppShell>
  );
}
